import * as React from 'react';
import { useState } from 'react';

import { COLORS, useSnackbarContext } from '@clutter/clean';

import {
  INDUSTRY_OPTIONS,
  INITIAL_FORM_DATA,
  LOCATION_OPTIONS,
  PartnershipLeadFormData,
} from '@root/constants/partnerships';

import { ResponsiveImage } from '@shared/responsive_image';

import { useCreatePartnershipLead } from '@utils/hooks/partnerships/use_create_partnership_lead';
import {
  FIELD_CONSTRAINTS,
  type FieldValidationErrors,
  type ValidationResult,
  validateEmail,
  validateMessage,
  validateName,
  validateOrganization,
  validatePartnershipForm,
  validatePhone,
} from '@utils/validation/partnership_form_validation';

// Import mailbox image
import mailboxImage from '@images_responsive/partnerships/mailbox.png';

import {
  ContactSection,
  Container,
  ContentWrapper,
  EnvelopeIconContainer,
  FormContainer,
  FormError,
  FormGroup,
  FormInput,
  FormRow,
  FormSelect,
  FormTextarea,
  LeftSection,
  RightSection,
  SectionHeading,
  SubmitButton,
} from './contact_form.styles';

export const ContactFormComponent: React.FC = () => {
  const { addSnack } = useSnackbarContext();
  const createPartnershipLead = useCreatePartnershipLead();
  const [formData, setFormData] =
    useState<PartnershipLeadFormData>(INITIAL_FORM_DATA);
  const [formError, setFormError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<FieldValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear form error when user starts typing
    if (formError) {
      setFormError('');
    }

    // Clear select field errors when value changes
    if (name === 'industry' || name === 'location') {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }

    // Real-time validation for touched fields
    if (touchedFields.has(name)) {
      validateField(name, value);
    }
  };

  const handleInputBlur = (fieldName: string, value: string) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName));
    validateField(fieldName, value);
  };

  const validateField = (fieldName: string, value: string) => {
    let validationResult: ValidationResult;

    switch (fieldName) {
      case 'name':
        validationResult = validateName(value);
        break;
      case 'email':
        validationResult = validateEmail(value);
        break;
      case 'phone':
        validationResult = validatePhone(value);
        break;
      case 'organization':
        validationResult = validateOrganization(value);
        break;
      case 'message':
        validationResult = validateMessage(value);
        break;
      default:
        return;
    }

    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      if (validationResult.isValid) {
        delete newErrors[fieldName as keyof FieldValidationErrors];
      } else {
        newErrors[fieldName as keyof FieldValidationErrors] =
          validationResult.errorMessage;
      }
      return newErrors;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    // Validate all fields
    const { isValid, errors } = validatePartnershipForm(formData);

    if (!isValid) {
      setFieldErrors(errors);
      setFormError('Please fix the errors below before submitting.');
      setIsSubmitting(false);
      return;
    }

    // Clear any existing field errors if validation passes
    setFieldErrors({});

    try {
      const result = await createPartnershipLead(formData);

      if (result.success) {
        // Show success toast
        addSnack({
          key: 'partnership-success',
          content: 'Thank you for your interest! We’ll be in touch soon.',
        });

        // Reset form
        setFormData(INITIAL_FORM_DATA);
        setTouchedFields(new Set());
      } else {
        setFormError(result.error || 'An error occurred. Please try again.');
      }
    } catch (error) {
      setFormError(
        'Network error. Please check your connection and try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection>
      <Container>
        <ContentWrapper>
          <LeftSection>
            <EnvelopeIconContainer>
              <ResponsiveImage image={mailboxImage} alt="Mailbox" />
            </EnvelopeIconContainer>
          </LeftSection>

          <RightSection>
            <SectionHeading>Get in touch</SectionHeading>

            <FormContainer onSubmit={handleSubmit}>
              {formError && <FormError>{formError}</FormError>}

              <FormGroup>
                <FormInput
                  type="text"
                  name="name"
                  placeholder="First & last name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={(e) => handleInputBlur('name', e.target.value)}
                  minLength={FIELD_CONSTRAINTS.NAME.MIN_LENGTH}
                  maxLength={FIELD_CONSTRAINTS.NAME.MAX_LENGTH}
                  required
                  style={{
                    borderColor: fieldErrors.name ? COLORS.toucan : undefined,
                  }}
                />
                {fieldErrors.name && <FormError>{fieldErrors.name}</FormError>}
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <FormInput
                    type="email"
                    name="email"
                    placeholder="Business email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={(e) => handleInputBlur('email', e.target.value)}
                    minLength={FIELD_CONSTRAINTS.EMAIL.MIN_LENGTH}
                    maxLength={FIELD_CONSTRAINTS.EMAIL.MAX_LENGTH}
                    required
                    style={{
                      borderColor: fieldErrors.email
                        ? COLORS.toucan
                        : undefined,
                    }}
                  />
                  {fieldErrors.email && (
                    <FormError>{fieldErrors.email}</FormError>
                  )}
                </FormGroup>
                <FormGroup>
                  <FormInput
                    type="tel"
                    name="phone"
                    placeholder="Phone number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={(e) => handleInputBlur('phone', e.target.value)}
                    minLength={FIELD_CONSTRAINTS.PHONE.MIN_LENGTH}
                    maxLength={FIELD_CONSTRAINTS.PHONE.MAX_LENGTH}
                    required
                    style={{
                      borderColor: fieldErrors.phone
                        ? COLORS.toucan
                        : undefined,
                    }}
                  />
                  {fieldErrors.phone && (
                    <FormError>{fieldErrors.phone}</FormError>
                  )}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <FormInput
                  type="text"
                  name="organization"
                  placeholder="Organization (Company/School)"
                  value={formData.organization}
                  onChange={handleInputChange}
                  onBlur={(e) =>
                    handleInputBlur('organization', e.target.value)
                  }
                  minLength={FIELD_CONSTRAINTS.ORGANIZATION.MIN_LENGTH}
                  maxLength={FIELD_CONSTRAINTS.ORGANIZATION.MAX_LENGTH}
                  required
                  style={{
                    borderColor: fieldErrors.organization
                      ? COLORS.toucan
                      : undefined,
                  }}
                />
                {fieldErrors.organization && (
                  <FormError>{fieldErrors.organization}</FormError>
                )}
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <FormSelect
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    required
                    style={{
                      borderColor: fieldErrors.industry
                        ? COLORS.toucan
                        : undefined,
                    }}
                  >
                    <option value="">Select industry</option>
                    {INDUSTRY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FormSelect>
                  {fieldErrors.industry && (
                    <FormError>{fieldErrors.industry}</FormError>
                  )}
                </FormGroup>
                <FormGroup>
                  <FormSelect
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    style={{
                      borderColor: fieldErrors.location
                        ? COLORS.toucan
                        : undefined,
                    }}
                  >
                    <option value="">Select location</option>
                    {LOCATION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FormSelect>
                  {fieldErrors.location && (
                    <FormError>{fieldErrors.location}</FormError>
                  )}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <FormTextarea
                  name="message"
                  placeholder="(Optional) Tell us a bit about what you’re looking for"
                  value={formData.message}
                  onChange={handleInputChange}
                  onBlur={(e) => handleInputBlur('message', e.target.value)}
                  maxLength={FIELD_CONSTRAINTS.MESSAGE.MAX_LENGTH}
                  rows={4}
                  style={{
                    borderColor: fieldErrors.message
                      ? COLORS.toucan
                      : undefined,
                  }}
                />
                {fieldErrors.message && (
                  <FormError>{fieldErrors.message}</FormError>
                )}
                <div
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    textAlign: 'right',
                    marginTop: '4px',
                  }}
                >
                  {formData.message.length}/
                  {FIELD_CONSTRAINTS.MESSAGE.MAX_LENGTH}
                </div>
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </SubmitButton>
            </FormContainer>
          </RightSection>
        </ContentWrapper>
      </Container>
    </ContactSection>
  );
};
